import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Inbox, MessageSquareReply, RefreshCw, Loader2, Send, ArrowLeft, Building2, ExternalLink, Mail, MailOpen, Trash2, CheckCircle2,
  AlertTriangle, Paperclip, Reply, CornerDownRight, Ban, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { api, errorMessage, formatDateTime } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STATUS_OPTIONS = [
  { value: "ikke_kontaktet", label: "Ikke kontaktet" },
  { value: "kontaktet", label: "Kontaktet" },
  { value: "interessert", label: "Interessert" },
  { value: "registrert", label: "Registrert på Right in" },
  { value: "ikke_aktuell", label: "Ikke aktuell" },
];
const STATUS_STYLES = {
  ikke_kontaktet: "bg-secondary text-secondary-foreground border-border",
  kontaktet: "bg-[hsl(var(--info))]/15 text-[hsl(var(--info))] border-[hsl(var(--info))]/30",
  interessert: "bg-[hsl(var(--warning))]/20 text-[hsl(var(--warning-foreground))] border-[hsl(var(--warning))]/40",
  registrert: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30",
  ikke_aktuell: "bg-muted text-muted-foreground border-border line-through",
};
const FILTERS = [
  { value: "unread", label: "Uleste" },
  { value: "replies", label: "Alle svar" },
  { value: "answered", label: "Besvart" },
  { value: "unmatched", label: "Uten treff" },
  { value: "auto", label: "Automatiske" },
];
const KIND_BADGE = {
  auto: "bg-muted text-muted-foreground border-border",
  bounce: "bg-[hsl(var(--danger))]/10 text-[hsl(var(--danger))] border-[hsl(var(--danger))]/30",
};

const displayFrom = (r) => (r.from_name ? `${r.from_name} <${r.from_email}>` : r.from_email || "(ukjent avsender)");

/** Svar-innboks (admin): les svar på utsendingene og svar direkte fra Right in – samtalen lagres på organisasjonen. */
export default function AdminRepliesPage() {
  const { organizer } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState("unread");
  const [data, setData] = useState(null);
  const [listError, setListError] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(searchParams.get("reply") || "");
  const [mobileDetail, setMobileDetail] = useState(!!searchParams.get("reply"));
  const initialFilterDone = useRef(false);

  const load = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    setListError("");
    api.get("/admin/prospects/replies", { params: { status: filter, limit: 200 } })
      .then((res) => setData(res.data))
      .catch((err) => setListError(errorMessage(err)))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => {
    if (!organizer?.is_admin) return;
    load();
  }, [organizer?.is_admin, load]);

  // Dyplenke ?reply=<id>: vis «Alle svar» så tråden er i listen selv om den er lest
  useEffect(() => {
    if (initialFilterDone.current) return;
    initialFilterDone.current = true;
    if (searchParams.get("reply")) setFilter("replies");
  }, [searchParams]);

  const items = data?.items || [];
  const counts = data?.counts || {};

  const select = (id) => {
    setSelectedId(id);
    setMobileDetail(true);
    const next = new URLSearchParams(searchParams);
    if (id) next.set("reply", id); else next.delete("reply");
    setSearchParams(next, { replace: true });
  };

  const patchItem = (id, patch) => setData((prev) => (prev ? { ...prev, items: prev.items.map((i) => (i.id === id ? { ...i, ...patch } : i)) } : prev));

  if (!organizer?.is_admin) {
    return (
      <DashboardLayout>
        <p className="text-muted-foreground" data-testid="admin-replies-denied">Denne siden krever plattform-administrator.</p>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl space-y-5" data-testid="admin-replies-page">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight flex items-center gap-2">
              <Inbox className="w-7 h-7 text-brand" /> Svar (admin)
              {counts.unread > 0 && <Badge className="bg-brand text-brand-foreground hover:bg-brand" data-testid="admin-replies-unread-badge">{counts.unread} uleste</Badge>}
            </h1>
            <p className="text-muted-foreground mt-1">
              Svar på e-postene fra kontaktregisteret. Les, svar direkte fra Right in og sett status – hele samtalen lagres på organisasjonen.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => load()} disabled={loading} data-testid="admin-replies-refresh">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Oppdater
          </Button>
        </div>

        <Tabs value={filter} onValueChange={(v) => { setFilter(v); setMobileDetail(false); }}>
          <TabsList className="h-9 flex-wrap justify-start" data-testid="admin-replies-filters">
            {FILTERS.map((f) => {
              const n = f.value === "unread" ? counts.unread : f.value === "answered" ? counts.answered : f.value === "unmatched" ? counts.unmatched : f.value === "auto" ? counts.auto : undefined;
              return (
                <TabsTrigger key={f.value} value={f.value} className="text-xs sm:text-sm px-2.5" data-testid={`admin-replies-filter-${f.value}`}>
                  {f.label}{typeof n === "number" ? ` (${n})` : ""}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
          {/* Liste */}
          <div className={`lg:col-span-5 ${mobileDetail && selectedId ? "hidden lg:block" : ""}`} data-testid="admin-replies-list-pane">
            {loading && !data ? (
              <div className="space-y-2" data-testid="admin-replies-loading">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20 w-full rounded-xl" />)}</div>
            ) : listError ? (
              <div className="rounded-xl border border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger))]/10 p-4 text-sm" data-testid="admin-replies-error">
                <p className="font-medium flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Kunne ikke hente svar</p>
                <p className="text-muted-foreground mt-1">{listError}</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={() => load()} data-testid="admin-replies-retry">Prøv igjen</Button>
              </div>
            ) : items.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border p-8 text-center space-y-2" data-testid="admin-replies-empty">
                <Inbox className="w-8 h-8 mx-auto text-muted-foreground" />
                <p className="font-medium">{filter === "unread" ? "Ingen uleste svar" : "Ingen svar her"}</p>
                <p className="text-sm text-muted-foreground">
                  {filter === "unread"
                    ? "Alt er lest. Nye svar dukker opp her når organisasjoner svarer på e-postene dine."
                    : "Svar kommer inn automatisk når svar-sporing er satt opp i produksjon (Utsendinger → Svar-sporing)."}
                </p>
                <Button variant="outline" size="sm" onClick={() => navigate("/dashboard/kontaktregister-admin")} data-testid="admin-replies-goto-register">
                  <Building2 className="w-4 h-4 mr-1.5" /> Til kontaktregisteret
                </Button>
              </div>
            ) : (
              <ul className="space-y-1.5" data-testid="admin-replies-list">
                {items.map((r) => {
                  const active = r.id === selectedId;
                  return (
                    <li key={r.id}>
                      <button
                        type="button"
                        onClick={() => select(r.id)}
                        className={`w-full text-left rounded-xl border px-3 py-2.5 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
                          ${active ? "border-brand bg-brand-soft/50" : "border-border bg-card hover:bg-secondary/60"} ${r.unread ? "border-l-4 border-l-brand" : ""}`}
                        data-testid={`admin-reply-row-${r.id}`}
                        data-unread={r.unread ? "true" : "false"}
                      >
                        <div className="flex items-start gap-2">
                          <div className="mt-0.5 shrink-0 text-muted-foreground">
                            {r.kind !== "reply" ? <Ban className="w-4 h-4" /> : r.answers_count > 0 ? <CheckCircle2 className="w-4 h-4 text-[hsl(var(--success))]" /> : r.unread ? <Mail className="w-4 h-4 text-brand" /> : <MailOpen className="w-4 h-4" />}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center justify-between gap-x-2">
                              <span className={`truncate text-sm ${r.unread ? "font-semibold" : "font-medium"}`}>{r.from_name || r.from_email}</span>
                              <span className="text-[11px] tabular-nums text-muted-foreground shrink-0">{formatDateTime(r.received_at)}</span>
                            </div>
                            <div className="text-xs text-muted-foreground truncate flex items-center gap-1">
                              {r.org_name ? <><Building2 className="w-3 h-3 shrink-0" /> {r.org_name}</> : <Badge variant="outline" className="text-[10px] font-normal bg-[hsl(var(--danger))]/10 text-[hsl(var(--danger))] border-[hsl(var(--danger))]/30">Uten treff</Badge>}
                              {r.kind !== "reply" && <Badge variant="outline" className={`text-[10px] font-normal ${KIND_BADGE[r.kind] || ""}`}>{r.kind_label}</Badge>}
                            </div>
                            <p className={`text-sm truncate ${r.unread ? "font-medium" : ""}`}>{r.subject || "(uten emne)"}</p>
                            <p className="text-xs text-muted-foreground line-clamp-1">{r.excerpt}</p>
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Tråd */}
          <div className={`lg:col-span-7 ${!mobileDetail || !selectedId ? "hidden lg:block" : ""}`} data-testid="admin-replies-detail-pane">
            {selectedId ? (
              <ReplyThread
                key={selectedId}
                replyId={selectedId}
                onBack={() => setMobileDetail(false)}
                onRead={(read) => { patchItem(selectedId, { unread: !read }); setData((prev) => (prev ? { ...prev, counts: { ...prev.counts, unread: Math.max(0, (prev.counts.unread || 0) + (read ? -1 : 1)) } } : prev)); }}
                onAnswered={(det) => { patchItem(selectedId, { answers_count: det.reply.answers_count, unread: false, answered_at: det.reply.answered_at }); load(true); }}
                onDeleted={() => { setSelectedId(""); setMobileDetail(false); load(true); }}
                onOrgStatus={() => load(true)}
              />
            ) : (
              <div className="hidden lg:flex rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground flex-col items-center gap-2" data-testid="admin-replies-no-selection">
                <MessageSquareReply className="w-8 h-8" />
                <p className="text-sm">Velg et svar i listen for å lese tråden og svare.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

/** Én tråd: organisasjon, e-posten de svarte på, svaret, dine svar – og skrivefelt. */
function ReplyThread({ replyId, onBack, onRead, onAnswered, onDeleted, onOrgStatus }) {
  const navigate = useNavigate();
  const [det, setDet] = useState(null);
  const [error, setError] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [statusWorking, setStatusWorking] = useState(false);
  const markedRef = useRef(false);

  const load = useCallback(() => {
    setError("");
    api.get(`/admin/prospects/replies/${replyId}`)
      .then((res) => {
        setDet(res.data);
        setSubject((s) => s || res.data.draft.subject);
        setBody((b) => b || res.data.draft.body);
        // Åpnet = lest
        if (!markedRef.current && res.data.reply.kind === "reply" && !res.data.reply.read_at) {
          markedRef.current = true;
          api.post(`/admin/prospects/replies/${replyId}/read`, { read: true })
            .then(() => { setDet((d) => (d ? { ...d, reply: { ...d.reply, read_at: new Date().toISOString(), unread: false } } : d)); onRead(true); })
            .catch(() => {});
        }
      })
      .catch((err) => setError(errorMessage(err)));
  }, [replyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const toggleRead = async () => {
    const read = !det.reply.read_at;
    try {
      await api.post(`/admin/prospects/replies/${replyId}/read`, { read });
      setDet((d) => ({ ...d, reply: { ...d.reply, read_at: read ? new Date().toISOString() : "", unread: !read } }));
      onRead(read);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const setStatus = async (value) => {
    if (!det?.org) return;
    setStatusWorking(true);
    try {
      const res = await api.patch(`/admin/prospects/${det.org.id}`, { followup_status: value });
      setDet((d) => ({ ...d, org: { ...d.org, followup_status: res.data.followup_status || value, followup_label: res.data.followup_label || STATUS_OPTIONS.find((s) => s.value === value)?.label } }));
      toast.success(`Status satt til «${STATUS_OPTIONS.find((s) => s.value === value)?.label}»`);
      onOrgStatus();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setStatusWorking(false);
    }
  };

  const send = async () => {
    setSending(true);
    try {
      const res = await api.post(`/admin/prospects/replies/${replyId}/answer`, { subject, body });
      setDet(res.data);
      setBody(res.data.draft.body);
      toast.success(`Svaret er sendt til ${res.data.reply.from_email}`);
      onAnswered(res.data);
    } catch (err) {
      toast.error(errorMessage(err));
      load();
    } finally {
      setSending(false);
    }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/prospects/replies/${replyId}`);
      toast.success("Svaret er slettet");
      setConfirmDelete(false);
      onDeleted();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  if (error) {
    return (
      <div className="rounded-xl border border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger))]/10 p-4 text-sm" data-testid="admin-reply-thread-error">
        <Button variant="ghost" size="sm" className="lg:hidden mb-2 -ml-2" onClick={onBack}><ArrowLeft className="w-4 h-4 mr-1" /> Tilbake</Button>
        <p className="font-medium flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Kunne ikke hente tråden</p>
        <p className="text-muted-foreground mt-1">{error}</p>
        <Button variant="outline" size="sm" className="mt-3" onClick={load} data-testid="admin-reply-thread-retry">Prøv igjen</Button>
      </div>
    );
  }
  if (!det) {
    return <div className="space-y-3" data-testid="admin-reply-thread-loading"><Skeleton className="h-24 w-full rounded-xl" /><Skeleton className="h-40 w-full rounded-xl" /><Skeleton className="h-48 w-full rounded-xl" /></div>;
  }

  const { reply, org, original } = det;
  const answers = (reply.answers || []).filter((a) => a.ok);
  const failedAnswers = (reply.answers || []).filter((a) => !a.ok);
  const canSend = det.can_answer && det.enabled && subject.trim() && body.trim() && !sending;

  return (
    <div className="space-y-4" data-testid="admin-reply-thread">
      {/* Topp: avsender, org, handlinger */}
      <div className="rounded-xl border border-border bg-card p-4 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0 space-y-1">
            <Button variant="ghost" size="sm" className="lg:hidden -ml-2 h-8" onClick={onBack} data-testid="admin-reply-back"><ArrowLeft className="w-4 h-4 mr-1" /> Tilbake til listen</Button>
            <h2 className="font-heading font-semibold text-lg leading-tight break-words" data-testid="admin-reply-subject">{reply.subject || "(uten emne)"}</h2>
            <p className="text-sm break-all" data-testid="admin-reply-from"><span className="text-muted-foreground">Fra</span> {displayFrom(reply)}</p>
            <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span>Mottatt {formatDateTime(reply.received_at)}</span>
              {reply.kind !== "reply" && <Badge variant="outline" className={`text-[10px] font-normal ${KIND_BADGE[reply.kind] || ""}`}>{reply.kind_label}</Badge>}
              {reply.status_changed && <Badge variant="outline" className={`text-[10px] font-normal ${STATUS_STYLES.interessert}`}><Sparkles className="w-3 h-3 mr-1" /> Satt til Interessert automatisk</Badge>}
              {reply.matched_by && <span>· funnet via {reply.matched_by === "token" ? "svaradressen" : "avsenderadressen"}</span>}
              {reply.campaign_subject && <span>· svar på utsendingen «{reply.campaign_subject}»</span>}
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 shrink-0">
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={toggleRead} data-testid="admin-reply-toggle-read">
              {reply.read_at ? <><Mail className="w-3.5 h-3.5 mr-1" /> Merk som ulest</> : <><MailOpen className="w-3.5 h-3.5 mr-1" /> Merk som lest</>}
            </Button>
            <Button variant="outline" size="sm" className="h-8 text-xs text-[hsl(var(--danger))] border-[hsl(var(--danger))]/40 hover:bg-[hsl(var(--danger))]/10 hover:text-[hsl(var(--danger))]" onClick={() => setConfirmDelete(true)} data-testid="admin-reply-delete">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Slett
            </Button>
          </div>
        </div>

        {org ? (
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border border-border bg-secondary/40 px-3 py-2 text-sm" data-testid="admin-reply-org">
            <div className="min-w-0 flex-1 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-brand shrink-0" />
              <div className="min-w-0">
                <p className="font-medium truncate">{org.name}</p>
                <p className="text-xs text-muted-foreground truncate">{org.kommune}{org.email ? ` · ${org.email}` : ""}{org.replies_count > 1 ? ` · ${org.replies_count} svar totalt` : ""}</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <Select value={org.followup_status} onValueChange={setStatus} disabled={statusWorking}>
                <SelectTrigger className="h-8 w-[190px] bg-background text-xs" data-testid="admin-reply-org-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
              <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => navigate(`/dashboard/kontaktregister-admin?org=${org.id}`)} data-testid="admin-reply-open-org">
                <ExternalLink className="w-3.5 h-3.5 mr-1" /> Åpne
              </Button>
            </div>
          </div>
        ) : (
          <Alert className="border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10" data-testid="admin-reply-no-org">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle className="text-sm">Fant ikke avsenderen i kontaktregisteret</AlertTitle>
            <AlertDescription className="text-xs">Svaret kom uten gyldig svarkode og adressen finnes ikke på noen organisasjon. Du kan likevel svare – men statusen på en organisasjon blir ikke oppdatert.</AlertDescription>
          </Alert>
        )}
      </div>

      {/* Tråden */}
      <div className="space-y-3" data-testid="admin-reply-messages">
        {(original.subject || original.body) && (
          <div className="rounded-xl border border-border bg-secondary/30 p-4 text-sm" data-testid="admin-reply-original">
            <p className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-2 mb-1">
              <Send className="w-3.5 h-3.5" /> Din e-post{original.sent_by ? ` (${original.sent_by})` : ""}{original.at ? ` · ${formatDateTime(original.at)}` : ""}{original.to?.length ? ` · til ${original.to.join(", ")}` : ""}
            </p>
            <p className="font-medium">{original.subject}</p>
            {original.body && <p className="mt-1 whitespace-pre-line text-muted-foreground line-clamp-6">{original.body}</p>}
          </div>
        )}
        <div className="rounded-xl border border-brand/30 bg-brand-soft/30 p-4 text-sm" data-testid="admin-reply-message">
          <p className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-2 mb-1">
            <CornerDownRight className="w-3.5 h-3.5" /> {displayFrom(reply)} · {formatDateTime(reply.received_at)}
          </p>
          <p className="whitespace-pre-line break-words" data-testid="admin-reply-text">{reply.text || "(tom melding)"}</p>
          {reply.attachments?.length > 0 && (
            <p className="mt-2 text-xs text-muted-foreground flex flex-wrap items-center gap-1.5" data-testid="admin-reply-attachments">
              <Paperclip className="w-3.5 h-3.5" /> {reply.attachments.map((a) => a.name).join(", ")}
              {reply.forwarded_ok ? " · vedlegg videresendt til " + reply.forwarded_to : " · vedlegg ligger hos Brevo"}
            </p>
          )}
        </div>
        {answers.map((a) => (
          <div key={a.id} className="rounded-xl border border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/5 p-4 text-sm lg:ml-8" data-testid={`admin-reply-answer-${a.id}`}>
            <p className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-2 mb-1">
              <Reply className="w-3.5 h-3.5" /> {a.by_name || a.by} svarte · {formatDateTime(a.at)} · til {a.to}
            </p>
            <p className="font-medium">{a.subject}</p>
            <p className="mt-1 whitespace-pre-line break-words">{a.body}</p>
            {a.signature && <p className="mt-2 whitespace-pre-line text-xs text-muted-foreground">{a.signature}</p>}
          </div>
        ))}
        {failedAnswers.length > 0 && (
          <p className="text-xs text-[hsl(var(--danger))] lg:ml-8" data-testid="admin-reply-failed-answers">
            {failedAnswers.length} svar kunne ikke sendes ({failedAnswers[failedAnswers.length - 1].error}). Prøv igjen under.
          </p>
        )}
      </div>

      {/* Skriv svar */}
      <div className="rounded-xl border border-border bg-card p-4 space-y-3" data-testid="admin-reply-composer">
        <p className="font-heading font-semibold flex items-center gap-2"><MessageSquareReply className="w-4 h-4 text-brand" /> Svar fra Right in</p>
        {!det.can_answer ? (
          <p className="text-sm text-muted-foreground" data-testid="admin-reply-cannot-answer">Automatiske svar og feilmeldinger kan ikke besvares herfra.</p>
        ) : (
          <>
            {!det.enabled && (
              <Alert className="border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger))]/10" data-testid="admin-reply-email-disabled">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle className="text-sm">E-post er ikke konfigurert</AlertTitle>
                <AlertDescription className="text-xs">Serveren mangler SMTP-oppsett – svaret kan ikke sendes herfra.</AlertDescription>
              </Alert>
            )}
            <p className="text-xs text-muted-foreground break-all" data-testid="admin-reply-headers">
              <span className="inline-block w-14">Til</span> {reply.from_email}<br />
              <span className="inline-block w-14">Fra</span> {det.sender}<br />
              <span className="inline-block w-14">Svar til</span> {det.reply_to}{det.reply_to?.startsWith("svar+") ? " (svar-sporing – svaret deres kommer tilbake hit)" : ""}
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="reply-subject">Emne</Label>
              <Input id="reply-subject" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} data-testid="admin-reply-answer-subject" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reply-body">Svar</Label>
              <Textarea id="reply-body" rows={8} value={body} onChange={(e) => setBody(e.target.value)} maxLength={8000} className="font-sans text-sm" data-testid="admin-reply-answer-body" />
              <div className="rounded-md border border-dashed border-border bg-secondary/30 px-3 py-2 text-xs text-muted-foreground whitespace-pre-line" data-testid="admin-reply-signature">
                {det.signature}
                <span className="block mt-1 not-italic opacity-80">Signaturen og et sitat av meldingen legges til automatisk.</span>
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={send} disabled={!canSend} data-testid="admin-reply-send">
                {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />} Send svar
              </Button>
            </div>
          </>
        )}
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={(o) => !o && !deleting && setConfirmDelete(false)}>
        <AlertDialogContent data-testid="admin-reply-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Slette svaret?</AlertDialogTitle>
            <AlertDialogDescription>Svaret fra {reply.from_email} og dine svar i tråden fjernes fra Right in. Status på organisasjonen beholdes. E-poster som allerede er sendt påvirkes ikke.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="admin-reply-delete-no">Behold</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); remove(); }} disabled={deleting} className="bg-[hsl(var(--danger))] text-white hover:bg-[hsl(var(--danger))]/90" data-testid="admin-reply-delete-yes">
              {deleting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1.5" />} Slett
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
